import type { RuleSet } from "styled-components";
import { css } from "styled-components";
import { BREAKPOINTS } from "../../utils/constants";

type BreakpointKey = "xs" | "sm" | "md" | "lg" | "xl";
type ResponsiveValue<T> = T | Partial<Record<BreakpointKey, T>>;

export const numberToPx = (v: string | number): string => (typeof v === "string" ? v : `${v}px`);

export const getCssProperty = (val: string, element: Element = document.body): string =>
  getComputedStyle(element).getPropertyValue(val.split("(")[1].replace(")", ""));

const matchRegex = /^(.*?)%value(.*)$/;

export const cssVarToNumber = (
  val: string,
  unit = "px",
  element: Element = document.body
): number =>
  Number(
    getComputedStyle(element).getPropertyValue(val.split("(")[1].replace(")", "")).replace(unit, "")
  );

export const breakpointsEnhancer = (
  cssProp: string,
  values: ResponsiveValue<string | number> | undefined,
  regex?: string
): RuleSet | null => {
  let prefix = "";
  let suffix = "";

  if (regex) {
    const match = regex.match(matchRegex);
    if (match) {
      [, prefix, suffix] = match;
    }
  }

  if (cssProp.startsWith("--")) {
    if (typeof values === "string" || typeof values === "number") {
      return css`
        ${cssProp}: ${numberToPx(values)};
      `;
    }
    if (typeof values === "object" && values !== null) {
      const breakpointProps = Object.keys(values).reduce((mediaQueries, breakpoint) => {
        const value = values[breakpoint];
        let result = mediaQueries;
        result += `
            @media screen and (min-width: ${BREAKPOINTS[breakpoint]}px) {
              ${cssProp}: ${numberToPx(value)};
            }
            `;
        return result;
      }, "");
      return css([breakpointProps]);
    }
  }

  if (
    (typeof values === "object" || typeof values === "string" || typeof values === "number") &&
    values !== null
  ) {
    if (typeof values !== "object") {
      return css`
        ${cssProp}: ${prefix}${numberToPx(values)}${suffix};
      `;
    }
    const breakpointProps = Object.keys(values).reduce((mediaQueries, value) => {
      const [screenBreakpoint, cssPropBreakpoint] = [value, values[value]];
      let result = mediaQueries;
      result += `
          @media screen and (min-width: ${BREAKPOINTS[screenBreakpoint]}px) {
            ${cssProp}: ${prefix}${cssPropBreakpoint}${suffix};
          }
          `;
      return result;
    }, "");
    return css([breakpointProps]);
  }

  return null;
};
